import { DEV, resolveEnv } from '@fluentsms/utils';

const STORAGE_KEY_BRANCH = 'fluent:dev:graphql-branch';
const STORAGE_KEY_URI = 'fluent:dev:graphql-uri-override';
const STORAGE_KEY_WSS = 'fluent:dev:graphql-wss-override';
const STORAGE_KEY_FEATURE_BRANCHES = 'fluent:dev:feature-branches';
const STORAGE_KEY_SHOW_TOOLBAR = 'fluent:showDevToolbar';
const STORAGE_KEY_TOOLBAR_CORNER = 'fluent:dev:toolbar-corner';
const QUERY_PARAM = 'graphql-branch';
const QUERY_PARAM_FEATURES = 'feature-branches';
const QUERY_PARAM_SHOW_TOOLBAR = 'show-devtoolbar';

export const DEV_OVERRIDE_CHANGE_EVENT = 'fluent:dev-override-change';

export type ToolbarCorner =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'top-middle'
  | 'bottom-middle'
  | 'left-middle'
  | 'right-middle';

const DEFAULT_TOOLBAR_CORNER: ToolbarCorner = 'top-middle';
const VALID_CORNERS: ToolbarCorner[] = [
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
  'top-middle',
  'bottom-middle',
  'left-middle',
  'right-middle',
];

const BASE_HTTP_URL = 'https://dev.fluentapi.titlesolutions.com/graphql';
const BASE_WSS_URL = 'wss://dev.fluentwebsockets.titlesolutions.com/subscriptions';

const isDev = (): boolean => resolveEnv() === DEV;

const safeGetItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSetItem = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // localStorage unavailable (private browsing, quota exceeded)
  }
};

const safeRemoveItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // localStorage unavailable
  }
};

const notifyChange = (): void => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(DEV_OVERRIDE_CHANGE_EVENT));
};

export const getGraphqlBranch = (): string | null => {
  if (!isDev()) return null;
  return safeGetItem(STORAGE_KEY_BRANCH);
};

export const getUriOverride = (): string | null => {
  if (!isDev()) return null;
  return safeGetItem(STORAGE_KEY_URI);
};

export const getWssOverride = (): string | null => {
  if (!isDev()) return null;
  return safeGetItem(STORAGE_KEY_WSS);
};

export const setGraphqlBranch = (branch: string | number): void => {
  if (!isDev()) {
    console.warn('[GraphQL] Branch override is only available in DEV environment.');
    return;
  }

  const branchStr = String(branch);
  const uri = `${BASE_HTTP_URL}-${branchStr}`;
  const wss = `${BASE_WSS_URL}-${branchStr}`;

  safeSetItem(STORAGE_KEY_BRANCH, branchStr);
  safeSetItem(STORAGE_KEY_URI, uri);
  safeSetItem(STORAGE_KEY_WSS, wss);

  console.log('%c[GraphQL] Branch override set to %s', 'color: #f59e0b; font-weight: bold;', branchStr);
  console.log('  HTTP: %s', uri);
  console.log('  WSS:  %s', wss);
  console.log('  Next HTTP request will use the new endpoint. Refresh to reconnect WebSocket.');
  notifyChange();
};

export const clearGraphqlBranch = (): void => {
  safeRemoveItem(STORAGE_KEY_BRANCH);
  safeRemoveItem(STORAGE_KEY_URI);
  safeRemoveItem(STORAGE_KEY_WSS);

  console.log('%c[GraphQL] Branch override cleared. Using default DEV endpoint.', 'color: #10b981; font-weight: bold;');
  notifyChange();
};

export const getFeatureBranches = (): Record<string, string> | null => {
  if (!isDev()) return null;
  const raw = safeGetItem(STORAGE_KEY_FEATURE_BRANCHES);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Record<string, string>;
    return Object.keys(parsed).length > 0 ? parsed : null;
  } catch {
    return null;
  }
};

export const getFeatureBranchesHeader = (): string | null => {
  const branches = getFeatureBranches();
  if (!branches) return null;
  return Object.entries(branches)
    .map(([service, branch]) => `${service}=${branch}`)
    .join(',');
};

export const getShareableDevLink = (): string => {
  if (typeof window === 'undefined') return '';

  const url = new URL(window.location.href);

  const branch = getGraphqlBranch();
  if (branch) {
    url.searchParams.set(QUERY_PARAM, branch);
  } else {
    url.searchParams.delete(QUERY_PARAM);
  }

  const featureHeader = getFeatureBranchesHeader();
  if (featureHeader) {
    url.searchParams.set(QUERY_PARAM_FEATURES, featureHeader);
  } else {
    url.searchParams.delete(QUERY_PARAM_FEATURES);
  }

  return url.toString();
};

export const setFeatureBranches = (mapping: Record<string, string>): void => {
  if (!isDev()) {
    console.warn('[GraphQL] Feature branch overrides are only available in DEV environment.');
    return;
  }

  safeSetItem(STORAGE_KEY_FEATURE_BRANCHES, JSON.stringify(mapping));

  console.log('%c[GraphQL] Feature branches updated', 'color: #f59e0b; font-weight: bold;');
  console.log(
    '  Header: x-feature-branches: %s',
    Object.entries(mapping)
      .map(([s, b]) => `${s}=${b}`)
      .join(','),
  );
  notifyChange();
};

export const addFeatureBranch = (service: string, branch: string): void => {
  if (!isDev()) {
    console.warn('[GraphQL] Feature branch overrides are only available in DEV environment.');
    return;
  }

  const current = getFeatureBranches() ?? {};
  current[service] = branch;
  setFeatureBranches(current);
};

export const removeFeatureBranch = (service: string): void => {
  if (!isDev()) return;

  const current = getFeatureBranches() ?? {};
  delete current[service];

  if (Object.keys(current).length === 0) {
    safeRemoveItem(STORAGE_KEY_FEATURE_BRANCHES);
    console.log('%c[GraphQL] All feature branch overrides cleared.', 'color: #10b981; font-weight: bold;');
  } else {
    safeSetItem(STORAGE_KEY_FEATURE_BRANCHES, JSON.stringify(current));
    console.log('%c[GraphQL] Removed feature branch for "%s"', 'color: #10b981; font-weight: bold;', service);
  }
  notifyChange();
};

export const clearFeatureBranches = (): void => {
  safeRemoveItem(STORAGE_KEY_FEATURE_BRANCHES);
  console.log('%c[GraphQL] All feature branch overrides cleared.', 'color: #10b981; font-weight: bold;');
  notifyChange();
};

export const isDevToolbarVisible = (): boolean => safeGetItem(STORAGE_KEY_SHOW_TOOLBAR) !== 'false';

export const showDevToolbar = (): void => {
  safeSetItem(STORAGE_KEY_SHOW_TOOLBAR, 'true');
  console.log('%c[DevToolbar] Shown. Hide with window.__hideDevToolbar()', 'color: #10b981; font-weight: bold;');
  notifyChange();
};

export const hideDevToolbar = (): void => {
  safeSetItem(STORAGE_KEY_SHOW_TOOLBAR, 'false');
  console.log(
    `%c[DevToolbar] Hidden. Re-enable with ?${QUERY_PARAM_SHOW_TOOLBAR}=1 or window.__showDevToolbar()`,
    'color: #10b981; font-weight: bold;',
  );
  notifyChange();
};

export const getToolbarCorner = (): ToolbarCorner => {
  const stored = safeGetItem(STORAGE_KEY_TOOLBAR_CORNER);
  return VALID_CORNERS.includes(stored as ToolbarCorner) ? (stored as ToolbarCorner) : DEFAULT_TOOLBAR_CORNER;
};

export const setToolbarCorner = (corner: ToolbarCorner): void => {
  safeSetItem(STORAGE_KEY_TOOLBAR_CORNER, corner);
  notifyChange();
};

const detectQueryParam = (): void => {
  if (typeof window === 'undefined') return;
  if (!isDev()) return;

  const params = new URLSearchParams(window.location.search);
  let changed = false;

  const branch = params.get(QUERY_PARAM);
  if (branch) {
    setGraphqlBranch(branch);
    params.delete(QUERY_PARAM);
    changed = true;
  }

  const features = params.get(QUERY_PARAM_FEATURES);
  if (features) {
    const mapping: Record<string, string> = {};
    features.split(',').forEach((pair) => {
      const separatorIndex = pair.indexOf(':') !== -1 ? pair.indexOf(':') : pair.indexOf('=');
      const service = separatorIndex !== -1 ? pair.slice(0, separatorIndex) : '';
      const branchNum = separatorIndex !== -1 ? pair.slice(separatorIndex + 1) : '';
      if (service && branchNum) {
        mapping[service.trim()] = branchNum.trim();
      }
    });
    if (Object.keys(mapping).length > 0) {
      setFeatureBranches(mapping);
    }
    params.delete(QUERY_PARAM_FEATURES);
    changed = true;
  }

  if (params.has(QUERY_PARAM_SHOW_TOOLBAR)) {
    showDevToolbar();
    params.delete(QUERY_PARAM_SHOW_TOOLBAR);
    changed = true;
  }

  if (changed) {
    const newSearch = params.toString();
    const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}${window.location.hash}`;
    window.history.replaceState(null, '', newUrl);
  }
};

const logActiveBanner = (): void => {
  if (typeof window === 'undefined') return;
  if (!isDev()) return;

  const branch = getGraphqlBranch();
  if (branch) {
    console.log(
      '%c[GraphQL Override Active] Branch: %s',
      'background: #f59e0b; color: #000; padding: 2px 6px; border-radius: 3px; font-weight: bold;',
      branch,
    );
    console.log('  Pointing to: %s', getUriOverride());
    console.log('  Use window.__clearGraphqlBranch() or the DevToolbar to reset.');
  }

  const featureHeader = getFeatureBranchesHeader();
  if (featureHeader) {
    console.log(
      '%c[Feature Branches Active]',
      'background: #8b5cf6; color: #fff; padding: 2px 6px; border-radius: 3px; font-weight: bold;',
    );
    console.log('  x-feature-branches: %s', featureHeader);
    console.log('  Use window.__clearFeatureBranches() or the DevToolbar to reset.');
  }
};

const registerWindowHelpers = (): void => {
  if (typeof window === 'undefined') return;
  if (!isDev()) return;

  const w = window as Window & {
    __setGraphqlBranch?: typeof setGraphqlBranch;
    __clearGraphqlBranch?: typeof clearGraphqlBranch;
    __addFeatureBranch?: typeof addFeatureBranch;
    __removeFeatureBranch?: typeof removeFeatureBranch;
    __clearFeatureBranches?: typeof clearFeatureBranches;
    __getFeatureBranches?: typeof getFeatureBranches;
    __showDevToolbar?: typeof showDevToolbar;
    __hideDevToolbar?: typeof hideDevToolbar;
  };

  w.__setGraphqlBranch = setGraphqlBranch;
  w.__clearGraphqlBranch = clearGraphqlBranch;
  w.__addFeatureBranch = addFeatureBranch;
  w.__removeFeatureBranch = removeFeatureBranch;
  w.__clearFeatureBranches = clearFeatureBranches;
  w.__getFeatureBranches = getFeatureBranches;
  w.__showDevToolbar = showDevToolbar;
  w.__hideDevToolbar = hideDevToolbar;
};

if (isDev()) {
  detectQueryParam();
  logActiveBanner();
  registerWindowHelpers();
}
